import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Wine, Pencil, Trash2, ClipboardPlus, Minus, Warehouse } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Wines() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const { data: wines = [], isLoading } = useQuery({
    queryKey: ["wines", search],
    queryFn: async () => (await api.get("/wines", { params: { search: search || undefined } })).data,
  });

  const confirmDelete = async () => {
    try {
      await api.delete(`/wines/${toDelete.id}`);
      toast.success("Vino eliminado");
      qc.invalidateQueries({ queryKey: ["wines"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch {
      toast.error("No se pudo eliminar");
    } finally {
      setToDelete(null);
    }
  };

  const adjustStock = async (w, delta) => {
    try {
      const { data } = await api.post(`/wines/${w.id}/adjust-stock`, { delta });
      qc.setQueryData(["wines", search], (old) =>
        (old || []).map((x) => (x.id === w.id ? { ...x, cantidad_botellas: data.cantidad_botellas } : x))
      );
    } catch {
      toast.error("No se pudo ajustar el inventario");
    }
  };

  const ESTADO_COLOR = { "En Cava": "bg-borgona-50 text-borgona-700", "Abierta": "bg-dorado-50 text-dorado-700", "Terminada": "bg-grises-100 text-grises-500" };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-1">Colección</p>
          <h1 className="font-serif text-4xl text-negro">Mis Vinos</h1>
        </div>
        <Button data-testid="new-wine-btn" onClick={() => navigate("/app/vinos/nuevo")}
          className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300">
          <Plus className="w-4 h-4 mr-1" /> Registrar Vino
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 text-grises-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="wine-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, bodega o variedad..." className="pl-9 h-11" />
      </div>

      {isLoading ? (
        <p className="text-grises-400">Cargando...</p>
      ) : wines.length === 0 ? (
        <Card className="p-12 text-center border-grises-200 rounded-xl">
          <Wine className="w-12 h-12 text-borgona-300 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-grises-500 mb-5">No hay vinos registrados.</p>
          <Button data-testid="empty-new-wine-btn" onClick={() => navigate("/app/vinos/nuevo")}
            className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Registrar primer vino
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {wines.map((w) => (
            <Card key={w.id} data-testid={`wine-card-${w.id}`}
              className="overflow-hidden border-grises-200 rounded-xl hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-transform duration-300">
              <div className="h-40 bg-grises-100 relative">
                {w.foto_botella ? (
                  <img src={fileUrl(w.foto_botella)} alt={w.nombre} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-borgona-50 to-grises-100">
                    <Wine className="w-10 h-10 text-borgona-300" strokeWidth={1.25} />
                  </div>
                )}
                {w.tipo && <span className="absolute top-3 left-3 text-xs px-2.5 py-1 rounded-full bg-blanco/90 text-borgona-700 font-medium">{w.tipo}</span>}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg text-negro leading-tight">{w.nombre}</h3>
                <p className="text-sm text-grises-500 mb-1">{w.bodega || "—"}</p>
                <p className="text-xs text-grises-400 mb-3">{[w.pais, w.region, w.anada].filter(Boolean).join(" · ")}</p>

                <div className="flex items-center justify-between mb-3 rounded-xl bg-blanco-off px-3 py-2">
                  <div className="flex items-center gap-1.5 text-xs text-grises-500">
                    <Warehouse className="w-3.5 h-3.5" />
                    <span data-testid={`wine-stock-${w.id}`} className="font-medium text-negro">{w.cantidad_botellas ?? 0}</span>
                    <span>botella{(w.cantidad_botellas ?? 0) === 1 ? "" : "s"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`stock-minus-${w.id}`} onClick={() => adjustStock(w, -1)}
                      className="w-7 h-7 rounded-full border border-grises-300 flex items-center justify-center hover:bg-borgona-50 transition-colors disabled:opacity-40"
                      disabled={(w.cantidad_botellas ?? 0) <= 0} aria-label="Quitar botella">
                      <Minus className="w-3.5 h-3.5 text-grises-600" />
                    </button>
                    <button data-testid={`stock-plus-${w.id}`} onClick={() => adjustStock(w, 1)}
                      className="w-7 h-7 rounded-full border border-grises-300 flex items-center justify-center hover:bg-borgona-50 transition-colors" aria-label="Añadir botella">
                      <Plus className="w-3.5 h-3.5 text-grises-600" />
                    </button>
                  </div>
                </div>
                {w.estado_botella && (
                  <span className={`inline-block text-xs px-2.5 py-1 rounded-full mb-4 ${ESTADO_COLOR[w.estado_botella] || "bg-grises-100 text-grises-500"}`}>
                    {w.estado_botella}
                    {(w.anio_optimo_desde || w.anio_optimo_hasta) && (
                      <span className="text-grises-400"> · Óptimo {[w.anio_optimo_desde, w.anio_optimo_hasta].filter(Boolean).join("–")}</span>
                    )}
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <Button data-testid={`taste-wine-${w.id}`} size="sm"
                    onClick={() => navigate(`/app/catas/nueva?wine=${w.id}`)}
                    className="flex-1 bg-dorado-500 text-negro hover:bg-dorado-700 rounded-full text-xs transition-colors duration-300">
                    <ClipboardPlus className="w-3.5 h-3.5 mr-1" /> Catar
                  </Button>
                  <Button data-testid={`edit-wine-${w.id}`} size="icon" variant="outline"
                    onClick={() => navigate(`/app/vinos/${w.id}/editar`)}
                    className="rounded-full border-grises-300 h-9 w-9 hover:bg-borgona-50">
                    <Pencil className="w-4 h-4 text-grises-600" />
                  </Button>
                  <Button data-testid={`delete-wine-${w.id}`} size="icon" variant="outline"
                    onClick={() => setToDelete(w)}
                    className="rounded-full border-grises-300 h-9 w-9 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">¿Eliminar vino?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{toDelete?.nombre}" y todas sus catas asociadas. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-wine-btn" onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90 rounded-full">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
